import { get } from 'lodash';
import { IListViewColumn } from '../../../../shared/typings/interfaces/list-view.interface';
import { IAttendanceListStudent } from '../../../../store/effects/projections';
import { GRADIENT_TYPE } from './../../../../shared/constants/list-view/cell-gradient.constant';
import { COLUMN_DATA_TYPE } from './../../../../shared/constants/list-view/cell-type.constant';
import { IAttendanceMadLibModel } from './attendance-mad-lib.interface';

export const studentName: IListViewColumn = {
  human: 'Students',
  path: 'studentDetails.name.lastFirst',
  key: 'studentName',
  dataType: 'String',
  orderBy: 'asc',
  cellType: COLUMN_DATA_TYPE.SECTION_HEADER,
  cellConfig: {},
  getValueAtPath: (student: IAttendanceListStudent, madLibModel: IAttendanceMadLibModel, listType: string) => {
    const data = get(student, 'studentDetails.name.lastFirst');
    const studentId = get(student, 'studentId');
    const dependencies = { studentId };
    return { data, dependencies };
  },
};

export const lastYearOverAll: IListViewColumn = {
  human: 'Last Year Overall Attendance',
  key: 'lastYearOverall',
  cellType: COLUMN_DATA_TYPE.PERCENTAGE,
  cellConfig: {},
  dataType: 'Number',
  orderBy: 'desc',
  tooltip: "Student's final attendance rate at the end of last school year",
  getValueAtPath: (student: IAttendanceListStudent, madLibModel: IAttendanceMadLibModel, listType: string) => {
    let data = get(student, 'att.overallLastSy');
    const dependencies = {};
    return { data, dependencies };
  },
};

export const currentYtd: IListViewColumn = {
  human: 'Current YTD Attendance',
  key: 'ytd',
  cellType: COLUMN_DATA_TYPE.PERCENTAGE,
  cellConfig: {},
  dataType: 'Number',
  orderBy: 'desc',
  tooltip: 'Current year-to-date attendance as of previous school day',
  getValueAtPath: (student: IAttendanceListStudent, madLibModel: IAttendanceMadLibModel, listType: string) => {
    let data = get(student, 'att.ytd');
    const dependencies = {};
    return { data, dependencies };
  },
};

export const today: IListViewColumn = {
  human: 'Attendance Today',
  key: 'today',
  cellType: COLUMN_DATA_TYPE.GRADIENT_CUSTOM,
  cellConfig: { gradientRule: { type: GRADIENT_TYPE.ATTENDANCE_TODAY } },
  dataType: 'String',
  orderBy: 'desc',
  tooltip: 'Updated each school day by 2PM',
  getValueAtPath: (student: IAttendanceListStudent, madLibModel: IAttendanceMadLibModel, listType: string) => {
    const data = get(student, 'att.today');
    const dependencies = {};
    return { data, dependencies };
  },
};

export const thisTerm: IListViewColumn = {
  human: 'This term',
  key: 'thisTerm',
  cellType: COLUMN_DATA_TYPE.GRADIENT,
  cellConfig: {},
  dataType: 'Number',
  orderBy: 'desc',
  tooltip: 'Days missed since start of this term',
  getValueAtPath: (student: IAttendanceListStudent, madLibModel: IAttendanceMadLibModel, listType: string) => {
    let data = get(student, 'att.currTerm.abs');
    const dependencies = { pctPres: get(student, 'att.currTerm.pctPres') };
    return { data, dependencies };
  },
};

export const supports: IListViewColumn = {
  human: 'Attendance Supports',
  key: 'supports',
  cellType: COLUMN_DATA_TYPE.GENERIC,
  cellConfig: {},
  dataType: 'Number',
  orderBy: 'desc',
  getValueAtPath: (student: IAttendanceListStudent, madLibModel: IAttendanceMadLibModel, listType: string) => {
    const studentSupports: any[] = get(student, 'activeStudentSupports');
    const studentSupportsForCat = studentSupports.filter(
      studentSupport => studentSupport.support.category === listType,
    );
    const data = studentSupportsForCat.length;
    const dependencies = {};
    return { data, dependencies };
  },
};

export const flags: IListViewColumn = {
  human: 'Attendance Flags',
  path: null,
  key: 'flags',
  cellType: COLUMN_DATA_TYPE.FLAG,
  cellConfig: {},
  dataType: 'Array',
  orderBy: 'desc',
  getValueAtPath: (student: IAttendanceListStudent, madLibModel: IAttendanceMadLibModel, listType: string) => {
    const data = get(student, 'flags');
    const dependencies = {};
    return { data, dependencies };
  },
};

export const past5Days: IListViewColumn = {
  human: 'Past 5 days',
  key: 'fiveDayChange',
  cellType: COLUMN_DATA_TYPE.GRADIENT,
  cellConfig: {},
  dataType: 'Number',
  orderBy: 'desc',
  tooltip: 'Days missed in past 5 school days',
  getValueAtPath: (student: IAttendanceListStudent, madLibModel: IAttendanceMadLibModel, listType: string) => {
    let data = get(student, 'att.last5.abs');
    const dependencies = { pctPres: get(student, 'att.last5.pctPres') };
    return { data, dependencies };
  },
};

export const past20Days: IListViewColumn = {
  human: 'Past 20 days',
  key: 'twentyDayChange',
  cellType: COLUMN_DATA_TYPE.GRADIENT,
  cellConfig: {},
  dataType: 'Number',
  orderBy: 'desc',
  tooltip: 'Days missed in past 20 school days',
  getValueAtPath: (student: IAttendanceListStudent, madLibModel: IAttendanceMadLibModel, listType: string) => {
    let data = get(student, 'att.last20.abs');
    const dependencies = { pctPres: get(student, 'att.last20.pctPres') };
    return { data, dependencies };
  },
};

export const lastYearYtd: IListViewColumn = {
  human: 'Last year YTD Attendance',
  key: 'lastYear',
  cellType: COLUMN_DATA_TYPE.PERCENTAGE,
  cellConfig: {},
  dataType: 'Number',
  orderBy: 'desc',
  tooltip: 'YTD attendance this time last year',
  getValueAtPath: (student: IAttendanceListStudent, madLibModel: IAttendanceMadLibModel, listType: string) => {
    let data = get(student, 'att.ytdLastSy');
    const dependencies = {};
    return { data, dependencies };
  },
};

export const yearlyChange: IListViewColumn = {
  human: 'YTD Attendance Change',
  key: 'yearlyChange',
  cellType: COLUMN_DATA_TYPE.GRADIENT_CUSTOM,
  cellConfig: {
    gradientRule: {
      type: GRADIENT_TYPE.ATTENDANCE_YEARLY_CHANGE,
    },
  },
  dataType: 'Number',
  orderBy: 'desc',
  tooltip: 'Difference of current YTD attendance and YTD attendance this time last year',
  getValueAtPath: (student: IAttendanceListStudent, madLibModel: IAttendanceMadLibModel, listType: string) => {
    let data = get(student, 'att.currSy.pctChange');
    const dependencies = {};
    return { data, dependencies };
  },
};

export const assignedSupports: IListViewColumn = {
  human: 'Support Start Date',
  key: 'assigned',
  cellType: COLUMN_DATA_TYPE.DATE,
  cellConfig: {},
  dataType: 'Date',
  orderBy: 'asc',
  tooltip: "Student's start date in the support",
  getValueAtPath: (student: IAttendanceListStudent, madLibModel: IAttendanceMadLibModel, listType: string) => {
    const studentSupports: any[] = get(student, 'activeStudentSupports');
    const studentSupportsForCat = studentSupports.filter(
      studentSupport => studentSupport.support.category === listType,
    );
    const data = studentSupportsForCat;
    const dependencies = {};
    return { data, dependencies };
  },
};

export const admitDate: IListViewColumn = {
  human: 'Admit date',
  path: 'studentDetails.admitDate',
  key: 'admitDate',
  cellType: COLUMN_DATA_TYPE.DATE,
  cellConfig: {},
  dataType: 'Date',
  orderBy: 'asc',
  tooltip: 'Date admitted to the school',
  getValueAtPath: (student: IAttendanceListStudent, madLibModel: IAttendanceMadLibModel, listType: string) => {
    const data = get(student, 'studentDetails.admitDate');
    const dependencies = {};
    return { data, dependencies };
  },
};
